import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mail, Check, Trash2, X as XIcon, CheckSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import teamAvatar from '@/assets/cardsimp-team-avatar.png';
import { cn } from '@/lib/utils';
import { haptics } from '@/lib/haptics';
import { toast } from '@/lib/toast';
import type { ConversationSummary } from '@/hooks/useMyConversations';
import { useDeleteInquiry, useToggleInquiryHandled, useMarkInquiryRead, type Inquiry } from '@/hooks/useInquiries';
import { useDeleteNotification, useDeleteAllNotifications, type Notification } from '@/hooks/useNotifications';
import { getNotificationIcon } from './notificationIcon';
import { SwipeableInboxRow } from './SwipeableInboxRow';
import { formatUsdCents } from '@/lib/formatMoney';
import { isShadowedByConversation } from '@/lib/notificationShadowing';



interface ChatInboxListProps {
	onOpenTeam: () => void;
	onOpenDM: (conv: ConversationSummary) => void;
	onOpenInquiry: (inq: Inquiry) => void;
	onOpenNotification: (n: Notification) => void;
	markDmRead?: (conversationId: string) => void | Promise<void>;
	teamPreview?: string | null;
	teamUnread?: boolean;
	teamLastMessageAt?: string | null;
	conversations: ConversationSummary[];
	inquiries: Inquiry[];
	notifications: Notification[];
}

type RowKind = 'dm' | 'inquiry' | 'notification' | 'team';
type FilterKind = 'all' | 'alerts' | 'conversations' | 'closed';

interface Row {
	key: string;
	kind: RowKind;
	title: string;
	preview: string;
	timestamp: string | null;
	unread: boolean;
	needsReply?: boolean;
	kicker?: string | null;
	amountChip?: string | null;
	/** Inquiry pipeline status. Only set on inquiry rows. */
	inquiryStatus?: 'waiting' | 'won' | 'lost';
	avatar: React.ReactNode;
	onClick: () => void;
	onDelete?: () => void;
	onHandle?: () => void;
	handleLabel?: string;
}



const fmtOffer = (cents: number | null) =>
	cents == null ? null : `${formatUsdCents(cents)}`;

const matchesFilter = (row: Row, filter: FilterKind) => {
	// Sold/archived inquiries live in the Done tab and are hidden from all others.
	const isClosed = row.inquiryStatus === 'won' || row.inquiryStatus === 'lost';
	if (filter === 'closed') return isClosed;
	if (isClosed) return false;
	if (filter === 'all') return true;
	if (filter === 'alerts') return row.kind === 'notification';
	if (filter === 'conversations') return row.kind === 'inquiry' || row.kind === 'dm' || row.kind === 'team';
	return false;
};


/**
 * Unified inbox: blends notifications (alerts), guest inquiries (leads),
 * and P2P DMs (messages). CardSimp Team support is anchored as a floating
 * gold-hairline card at the bottom of the sheet — always reachable, never
 * crowding the active list.
 */
export const ChatInboxList = ({
	onOpenTeam,
	onOpenDM,
	onOpenInquiry,
	onOpenNotification,
	markDmRead,
	teamPreview,
	teamUnread,
	teamLastMessageAt,
	conversations,
	inquiries,
	notifications,
}: ChatInboxListProps) => {
	const [filter, setFilter] = useState<FilterKind>('all');
	const deleteInquiry = useDeleteInquiry();
	const toggleHandled = useToggleInquiryHandled();
	const markInquiryRead = useMarkInquiryRead();
	const deleteNotification = useDeleteNotification();

	// Bulk selection. Enters on long-press (mobile) or Shift-click (desktop).
	// Bulk bar mirrors InventoryToolbar's floating-action pattern.
	const [selectionMode, setSelectionMode] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const rowsByKey = useRef<Map<string, Row>>(new Map());

	const clearSelection = useCallback(() => {
		setSelected(new Set());
		setSelectionMode(false);
	}, []);

	const toggleSelected = useCallback((key: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const enterSelection = useCallback((key: string) => {
		haptics.impact();
		setSelectionMode(true);
		setSelected((prev) => {
			const next = new Set(prev);
			next.add(key);
			return next;
		});
	}, []);

	useEffect(() => {
		if (!selectionMode) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') clearSelection();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [selectionMode, clearSelection]);






	const dmRows: Row[] = useMemo(
		() =>
			conversations
				.filter((c) => c.kind === 'direct')
				.map((c) => {
					const initial = c.other_username?.charAt(0).toUpperCase() ?? '?';
					return {
						key: `dm:${c.id}`,
						kind: 'dm' as const,
						title: c.other_username ?? 'Collector',
						preview: c.last_message_preview ?? 'Tap to open',
						timestamp: c.last_message_at,
						unread: c.unread,
						needsReply: c.unread,
						avatar: (
							<Avatar className="h-12 w-12 shrink-0 ring-1 ring-white/[0.08]">
								<AvatarImage src={c.other_avatar_url ?? undefined} />
								<AvatarFallback className="text-xs bg-white/[0.06]">{initial}</AvatarFallback>
							</Avatar>
						),
						onClick: () => {
							if (c.unread) void markDmRead?.(c.id);
							onOpenDM(c);
						},
					};
				}),
		[conversations, onOpenDM, markDmRead]
	);

	const inquiryRows: Row[] = useMemo(
		() =>
			inquiries.map((inq) => {
				const offer = fmtOffer(inq.offer_cents);
				const previewBase = inq.message.replace(/\s+/g, ' ').trim();
				const unread = !inq.read_at;
				const handled = !!inq.handled_at;
				const status = inq.status ?? 'waiting';
				const needsReply = status === 'waiting' && !handled;
				const wonAmount = fmtOffer(inq.won_amount_cents);
				const kicker =
					status === 'won'
						? wonAmount
							? `Sold · ${wonAmount}`
							: 'Sold'
						: status === 'lost'
							? 'Archived'
							: offer
								? 'New offer'
								: null;
				return {
					key: `inq:${inq.id}`,
					kind: 'inquiry' as const,
					title: inq.guest_name || inq.guest_email,
					preview: previewBase || 'Inquiry',
					timestamp: inq.created_at,
					unread,
					needsReply,
					kicker,
					amountChip: status === 'waiting' ? offer : null,
					inquiryStatus: status,
					avatar: (
						<div
							className={cn(
								'h-12 w-12 rounded-full grid place-items-center shrink-0 border',
								status === 'won'
									? 'bg-success/15 text-success border-success/40'
									: status === 'lost'
										? 'bg-white/[0.02] text-muted-foreground border-white/[0.06]'
										: offer
											? 'bg-active-gold/12 text-active-gold border-active-gold/40'
											: 'bg-white/[0.04] text-muted-foreground border-dashed border-white/15'
							)}
							aria-hidden
						>
								<Mail className="h-[18px] w-[18px]" strokeWidth={1.75} />
							</div>
						),
						onClick: () => {
							if (unread) markInquiryRead.mutate({ id: inq.id, table: inq._table });
							onOpenInquiry(inq);
						},
						onDelete: () => deleteInquiry.mutate({ id: inq.id, table: inq._table }),
						onHandle: () =>
							toggleHandled.mutate({ id: inq.id, handled: !handled, table: inq._table }),
						handleLabel: handled ? 'Reopen' : 'Handle',
					};
				}),
		[inquiries, onOpenInquiry, deleteInquiry, toggleHandled, markInquiryRead]
	);



	// Suppress notification rows whose type is already represented by a
	// conversation/inquiry/DM row in this same inbox — otherwise users see
	// two near-duplicate entries (the alert + the actual thread row).
	// Canonical list lives in `src/lib/notificationShadowing.ts`.

	const notificationRows: Row[] = useMemo(
		() =>
			notifications
				.filter((n) => !isShadowedByConversation(n.type))
				.map((n) => ({
					key: `notif:${n.id}`,
					kind: 'notification' as const,
					title: n.title,
					preview: n.body ?? '',
					timestamp: n.created_at,
					unread: !n.read,
					needsReply: false,
					avatar: n.type === 'ebay_sale' ? (
						<div
							className="h-12 w-12 rounded-full grid place-items-center shrink-0 bg-white"
							aria-label="eBay"
						>
								<span className="text-[13px] font-bold leading-none tracking-tight italic">
									<span style={{ color: '#E53238' }}>e</span>
									<span style={{ color: '#0064D2' }}>b</span>
									<span style={{ color: '#F5AF02' }}>a</span>
									<span style={{ color: '#86B817' }}>y</span>
								</span>
							</div>
						) : (
							<div
								className="h-12 w-12 rounded-full grid place-items-center shrink-0 bg-white/[0.05] ring-1 ring-white/[0.08]"
								aria-hidden
							>
								{getNotificationIcon(n.type)}
							</div>
						),
						onClick: () => onOpenNotification(n),
						onDelete: () => deleteNotification.mutate(n.id),
					})),
		[notifications, onOpenNotification, deleteNotification]
	);


	const teamRow: Row = useMemo(
		() => ({
			key: 'team',
			kind: 'team' as const,
			title: 'CardSimp Team',
			preview: teamPreview || 'Tap to message support',
			// Fallback to "now" when no history so the team row sorts to the top
			// as the natural first-touch surface until real activity arrives.
			timestamp: teamLastMessageAt ?? new Date().toISOString(),
			unread: !!teamUnread,
			needsReply: false,
			avatar: (
				<Avatar className="h-12 w-12 shrink-0 ring-1 ring-active-gold/40">
					<AvatarImage src={teamAvatar} className="object-cover" />
					<AvatarFallback className="text-xs bg-white/[0.06]">CS</AvatarFallback>
				</Avatar>
			),
			onClick: () => onOpenTeam(),
		}),
		[teamPreview, teamLastMessageAt, teamUnread, onOpenTeam]
	);

	const allRows: Row[] = useMemo(
		() =>
			[teamRow, ...dmRows, ...inquiryRows, ...notificationRows].sort((a, b) => {
				const at = a.timestamp ? new Date(a.timestamp).getTime() : 0;
				const bt = b.timestamp ? new Date(b.timestamp).getTime() : 0;
				return bt - at;
			}),
		[teamRow, dmRows, inquiryRows, notificationRows]
	);

	const closedCount = useMemo(
		() =>
			inquiryRows.filter(
				(r) => r.inquiryStatus === 'won' || r.inquiryStatus === 'lost',
			).length,
		[inquiryRows]
	);

	const counts = useMemo(
		() => ({
			alerts: notificationRows.filter((r) => r.unread).length,
			conversations:
				inquiryRows.filter((r) => r.unread && r.inquiryStatus !== 'won' && r.inquiryStatus !== 'lost').length +
				dmRows.filter((r) => r.unread).length +
				(teamRow.unread ? 1 : 0),
		}),
		[dmRows, inquiryRows, notificationRows, teamRow.unread]
	);

	const filteredRows = allRows.filter((r) => matchesFilter(r, filter));

	const tabs: { id: FilterKind; label: string; unread: number }[] = [
		{ id: 'all', label: 'All', unread: 0 },
		{ id: 'alerts', label: 'Alerts', unread: counts.alerts },
		{ id: 'conversations', label: 'Messages', unread: counts.conversations },
		{ id: 'closed', label: 'Done', unread: 0 },
	];


	return (
		<div className="relative flex flex-col h-full">
			{/* Header */}
			<div className="shrink-0 px-5 pt-3.5 pb-2">
				<h2 className="font-logo text-[20.5px] leading-none text-foreground tracking-tight">
					Inbox
				</h2>
			</div>


			{/* Segmented tabs — inner tray */}
			<div className="shrink-0 px-4 pb-3">
				<div className={cn('grid gap-1 p-1 rounded-2xl bg-black/40 border border-white/[0.06]', tabs.length === 5 ? 'grid-cols-5' : 'grid-cols-4')}>
					{tabs.map((t) => {
						const active = filter === t.id;
						return (
							<button
								key={t.id}
								type="button"
								onClick={() => setFilter(t.id)}
								className={cn(
									'relative h-9 rounded-xl inline-flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors touch-manipulation select-none',
									active
										? 'bg-active-gold text-[#121416] shadow-[0_4px_14px_-4px_hsl(var(--active-gold)/0.35)]'
										: 'text-muted-foreground active:bg-white/[0.05]'
								)}
							>
								<span className="leading-none">{t.label}</span>
								{t.unread > 0 && (
									<span
										className={cn(
											'inline-flex h-4 min-w-4 px-1 rounded-full items-center justify-center text-[9px] font-black leading-none',
											active ? 'bg-[#121416]/25 text-[#121416]' : 'bg-active-gold/20 text-active-gold'
										)}
									>
										{t.unread > 9 ? '9+' : t.unread}
									</span>
								)}
							</button>
						);
					})}
				</div>
			</div>

			{/* Alerts clear-all bar */}
			{filter === 'alerts' && (
				<AlertsClearAllBar notificationCount={notificationRows.length} />
			)}

			{/* Scrollable list */}
			<div
				className="flex-1 overflow-y-auto px-3 pt-1 flex flex-col gap-2"
				style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}
			>



				{filteredRows.length === 0 ? (
					filter === 'all' ? null : (
						<EmptyStateFiltered filter={filter} onShowAll={() => setFilter('all')} />
					)
				) : (

					filteredRows.map((r) => {
						rowsByKey.current.set(r.key, r);
						return (
							<SwipeableInboxRow
								key={r.key}
								onDelete={r.onDelete}
								disabled={selectionMode}
								secondary={
									r.onHandle
										? { icon: Check, label: r.handleLabel || 'Handle', onAction: r.onHandle }
										: undefined
								}
							>
								<InboxRow
									row={r}
									selectionMode={selectionMode}
									selected={selected.has(r.key)}
									onEnterSelection={() => enterSelection(r.key)}
									onToggleSelected={() => toggleSelected(r.key)}
								/>
							</SwipeableInboxRow>
						);
					})
				)}
				<div aria-hidden className="h-1 shrink-0" />
			</div>

			{selectionMode && (
				<BulkActionBar
					count={selected.size}
					onCancel={clearSelection}
					onMarkDone={() => {
						let done = 0;
						selected.forEach((key) => {
							const row = rowsByKey.current.get(key);
							if (row?.onHandle) {
								row.onHandle();
								done += 1;
							}
						});
						if (done > 0) haptics.success();
						clearSelection();
					}}
					onDelete={() => {
						let done = 0;
						selected.forEach((key) => {
							const row = rowsByKey.current.get(key);
							if (row?.onDelete) {
								row.onDelete();
								done += 1;
							}
						});
						if (done > 0) haptics.impact();
						clearSelection();
					}}
				/>
			)}
		</div>
	);
};

const BulkActionBar = ({
	count,
	onCancel,
	onMarkDone,
	onDelete,
}: {
	count: number;
	onCancel: () => void;
	onMarkDone: () => void;
	onDelete: () => void;
}) => (
	<div
		className="absolute inset-x-3 z-30 rounded-2xl border border-white/[0.08] bg-black/85 backdrop-blur-xl shadow-[0_18px_40px_-14px_rgba(0,0,0,0.75)] animate-in slide-in-from-bottom-4 fade-in duration-200"
		style={{ bottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
	>
		<div className="flex items-center gap-1 px-2 py-2">
			<button
				type="button"
				onClick={onCancel}
				className="h-10 w-10 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors"
				aria-label="Cancel selection"
			>
				<XIcon className="h-4 w-4" />
			</button>
			<div className="text-[12.5px] font-semibold text-foreground px-1 tabular-nums min-w-[54px]">
				{count} selected
			</div>
			<div className="flex-1" />
			<button
				type="button"
				onClick={onMarkDone}
				disabled={count === 0}
				className="h-10 inline-flex items-center gap-1.5 rounded-full px-3.5 text-[12px] font-semibold text-foreground hover:bg-white/[0.05] disabled:opacity-40 transition-colors"
			>
				<Check className="h-4 w-4 text-active-gold" />
				<span>Mark done</span>
			</button>
			<button
				type="button"
				onClick={onDelete}
				disabled={count === 0}
				className="h-10 inline-flex items-center gap-1.5 rounded-full px-3.5 text-[12px] font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-40 transition-colors"
			>
				<Trash2 className="h-4 w-4" />
				<span>Delete</span>
			</button>
		</div>
	</div>
);

const AlertsClearAllBar = ({ notificationCount }: { notificationCount: number }) => {
	const deleteAll = useDeleteAllNotifications();
	const disabled = notificationCount === 0 || deleteAll.isPending;

	const onClear = () => {
		if (disabled) return;
		deleteAll.mutate(undefined, {
			onSuccess: () => {
				haptics.success();
				toast.success('All alerts cleared');
			},
			onError: () => {
				haptics.error();
				toast.error('Could not clear alerts');
			},
		});
	};

	return (
		<div className="shrink-0 px-4 pb-2 flex items-center justify-between">
			<span className="text-[12px] text-muted-foreground tabular-nums">
				{notificationCount} alert{notificationCount === 1 ? '' : 's'}
			</span>
			<button
				type="button"
				onClick={onClear}
				disabled={disabled}
				className="text-[12px] font-semibold text-active-gold hover:text-active-gold/80 disabled:opacity-40 disabled:hover:text-active-gold transition-colors"
			>
				{deleteAll.isPending ? 'Clearing…' : 'Clear All'}
			</button>
		</div>
	);
};



const LONG_PRESS_MS = 480;

const InboxRow = ({
	row,
	selectionMode,
	selected,
	onEnterSelection,
	onToggleSelected,
}: {
	row: Row;
	selectionMode: boolean;
	selected: boolean;
	onEnterSelection: () => void;
	onToggleSelected: () => void;
}) => {
	const showChip = !!row.amountChip;
	const timerRef = useRef<number | null>(null);
	const startX = useRef(0);
	const startY = useRef(0);
	const triggered = useRef(false);

	const clearTimer = () => {
		if (timerRef.current !== null) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	};

	const onPointerDown = (e: React.PointerEvent) => {
		if (selectionMode) return;
		triggered.current = false;
		startX.current = e.clientX;
		startY.current = e.clientY;
		clearTimer();
		timerRef.current = window.setTimeout(() => {
			triggered.current = true;
			onEnterSelection();
		}, LONG_PRESS_MS);
	};
	const onPointerMove = (e: React.PointerEvent) => {
		if (timerRef.current === null) return;
		if (Math.abs(e.clientX - startX.current) > 8 || Math.abs(e.clientY - startY.current) > 8) {
			clearTimer();
		}
	};
	const onPointerEnd = () => clearTimer();

	const handleClick = (e: React.MouseEvent) => {
		if (triggered.current) {
			triggered.current = false;
			e.preventDefault();
			return;
		}
		if (selectionMode) {
			e.preventDefault();
			onToggleSelected();
			return;
		}
		if (e.shiftKey) {
			e.preventDefault();
			onEnterSelection();
			return;
		}
		row.onClick();
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerEnd}
			onPointerCancel={onPointerEnd}
			onPointerLeave={onPointerEnd}
			className={cn(
				'relative w-full flex items-start gap-3 px-3 text-left group transition-colors rounded-2xl overflow-visible',
				row.kicker ? 'min-h-[92px] py-3.5' : 'min-h-[78px] py-3',
				selected
					? 'bg-active-gold/10 border border-active-gold/40'
					: row.unread
						? 'bg-white/[0.04] border border-white/[0.08] hover:border-active-gold/30'
						: 'border border-transparent hover:bg-white/[0.03]',
			)}
		>
			{selectionMode && (
				<span
					className={cn(
						'h-5 w-5 rounded-md border shrink-0 grid place-items-center transition-colors',
						selected
							? 'bg-active-gold border-active-gold text-[#121416]'
							: 'border-white/25 bg-white/[0.02]',
					)}
					aria-hidden
				>
					{selected && <CheckSquare className="h-3.5 w-3.5" strokeWidth={2.5} />}
				</span>
			)}

			{/* Avatar w/ optional gold $ chip — overflow-visible so the chip isn't clipped */}
			<div className="relative shrink-0 overflow-visible">
				{row.avatar}
				{showChip && !selectionMode && (
					<span
						className="absolute -top-1.5 -right-1.5 rounded-full px-1.5 py-0.5 text-[9px] font-black leading-none bg-active-gold text-[#121416] border-2 border-[#121416] shadow-[0_2px_8px_hsl(var(--active-gold)/0.35)]"
					>
						{row.amountChip}
					</span>
				)}
			</div>

			<div className="flex-1 min-w-0 self-stretch flex flex-col justify-center">
				{row.kicker && (
					<div className="text-[9.5px] font-bold uppercase tracking-widest text-active-gold mb-1 leading-[1.1]">
						{row.kicker}
					</div>
				)}
				<div className="flex items-baseline justify-between gap-2">
					<div className={cn(
						'text-[14px] leading-[1.2] truncate',
						row.unread ? 'font-bold text-foreground' : 'font-semibold text-foreground/90'
					)}>
						{row.title}
					</div>
					{row.timestamp && (
						<div className="text-[10.5px] leading-none text-muted-foreground shrink-0 tabular-nums">
							{formatDistanceToNow(new Date(row.timestamp), { addSuffix: false })}
						</div>
					)}
				</div>
				<div className="flex items-start justify-between gap-2 mt-1">
					<div
						className={cn(
							'text-[12.5px] leading-[1.35] truncate',
							row.unread ? 'text-foreground/75' : 'text-muted-foreground'
						)}
					>
						{row.preview}
					</div>
					{row.needsReply && !row.unread ? (
						<span className="shrink-0 text-[9px] leading-[1.2] font-bold uppercase tracking-widest text-active-gold pt-0.5">
							Needs reply
						</span>
					) : null}
				</div>
			</div>
			{!selectionMode && row.unread && (
				<span className="h-2 w-2 rounded-full bg-active-gold shadow-[0_0_10px_hsl(var(--active-gold)/0.7)] shrink-0" />
			)}
		</button>
	);
};


const EmptyStateAll = ({ onMessageSupport }: { onMessageSupport: () => void }) => (
	<div className="h-full flex flex-col items-center justify-center px-8 pb-12 text-center">
		<div className="text-[14px] font-medium text-foreground/80">Nothing here yet</div>
		<div className="text-[12.5px] text-muted-foreground mt-1.5 max-w-[260px]">
			Offers, inquiries, and chats will show up here.
		</div>
		<button
			type="button"
			onClick={onMessageSupport}
			className="mt-3 text-[12px] font-medium text-active-gold hover:text-active-gold/80 transition-colors"
		>
			Message the team
		</button>
	</div>
);

const FILTER_COPY: Record<Exclude<FilterKind, 'all' | 'support'>, { title: string; cta: string }> = {
	alerts: { title: 'All caught up', cta: 'See all activity' },
	conversations: { title: 'No messages yet', cta: 'See all activity' },
	closed: { title: 'Nothing done yet', cta: 'See all activity' },
};

const EmptyStateFiltered = ({
	filter,
	onShowAll,
}: {
	filter: Exclude<FilterKind, 'all' | 'support'>;
	onShowAll: () => void;
}) => {
	const copy = FILTER_COPY[filter];
	return (
		<div className="h-full flex flex-col items-center justify-center px-8 pb-12 text-center">
			<div className="text-[14px] font-medium text-foreground/80">{copy.title}</div>
			<button
				type="button"
				onClick={onShowAll}
				className="mt-3 text-[12px] font-medium text-active-gold hover:text-active-gold/80 transition-colors"
			>
				{copy.cta}
			</button>
		</div>
	);
};

export default ChatInboxList;
